import styled from "styled-components";
import { flVlUpdateDate } from "../../../Components/ConditionsSlideIn";

const Wrapper = styled.div`
  color: rgba(0, 0, 0, 0.6);
`;

const Title = styled.div`
  font-weight: 600;
  color: rgba(0, 0, 0, 1);
  margin-top: 32px;
`;

const Emph = styled.span`
  font-weight: 500;
  color: rgba(0, 0, 0, 1);
`;

const EmphSlight = styled.span`
  color: rgba(0, 0, 0, 0.85);
`;

const MiniBr = styled.div`
  padding-top: 12px;
`;

const AvailabilityNote = styled.div`
  padding-top: 12px;
  font-style: italic;
`;

const SubNote = styled.div`
  margin-top: 12px;
`;

const SubNoteBig = styled.div`
  margin-top: 32px;
`;

const ExternalLink = styled.a`
  text-decoration: underline;
  color: inherit;
`;

const HowVerificationWorksContent = () => (
  <Wrapper>
    Voter verification on Verified Voice works like this:
    <MiniBr />
    <EmphSlight>
      You verify that you own a phone number or email address via one-time code
      or secure link. If your verified contact appears in an active Florida
      voter registration in our copy of the {"state's"} voter file, you can
      participate in petitions as that voter.
    </EmphSlight>
    <AvailabilityNote>
      Currently available for Florida voters only.
    </AvailabilityNote>
    <Title>Your account</Title>
    <SubNote>
      You sign in with a one-time code sent by text, or a secure link sent by
      email. This confirms that you own that phone number or email address.
    </SubNote>
    <SubNote>
      Your account can hold more than one verified contact, and you can sign in
      with any of them. Reach out to us to add or remove contacts.
    </SubNote>
    <SubNote>
      Every stance you take on a petition is stored to your account.
    </SubNote>
    <Title>Linking your voter registration</Title>
    <SubNote>
      We keep and regularly update a copy of {"Florida's"} official voter
      records. If any of your verified contacts appear in an active voter record
      we have on file, you can link your account to that voter on your Account
      page.
    </SubNote>
    <SubNote>
      Once linked, you participate in petitions as that voter. Your stances are
      counted in each {"petition's"} verified tally of registered voter stances,
      including the breakdown by state House and Senate district.
    </SubNote>
    <SubNote>
      Your voter information is never shown publicly, and your stances appear
      only in a {"petition's"} tallies, never publicly identified as yours.
    </SubNote>
    <SubNote>
      Nothing you do on Verified Voice, including linking your account, affects
      your official voter registration. And petitions here {"aren't"} official
      state petitions; {"they're"} simply a way to show lawmakers and the public
      where verified voters stand.
    </SubNote>
    <Title>The voter file and full verification requirements</Title>
    <SubNote>
      Under Florida law, voter information is{" "}
      <ExternalLink
        href="https://dos.fl.gov/elections/for-voters/voter-registration/voter-information-is-public-record/"
        target="_blank"
      >
        public information
      </ExternalLink>{" "}
      with few exceptions. This is how we legally obtain our copy of the voter
      file, following{" "}
      <ExternalLink
        href="https://dos.fl.gov/elections/data-statistics/voter-registration-statistics/voter-extract-request/"
        target="_blank"
      >
        this procedure and schedule
      </ExternalLink>
      .
    </SubNote>
    <SubNote>
      Essentially, we obtain a copy of the list about once a month, and it
      includes voters registered up to the last day of the previous month. Our
      current voter list includes voters registered on or before{" "}
      {flVlUpdateDate}.
    </SubNote>
    <SubNote>
      We only verify voters who appear on our list meeting these conditions:
    </SubNote>
    <SubNote>
      <Emph>
        1. You are a Florida voter, registered to vote on or before{" "}
        {flVlUpdateDate}.
      </Emph>
      <MiniBr />
      If you registered after {flVlUpdateDate}, you {"won't"} appear in our
      current file yet. Check back after our next update, or contact us to be
      notified when you do appear.
    </SubNote>
    <SubNote>
      <Emph>
        2. Your registration includes a phone number or email address you own.
      </Emph>
      <MiniBr />
      Florida {"doesn't"} require a phone or email to register to vote, but we
      need one on your record to verify {"it's"} yours. If {"you're"} not sure
      what contact information is on your voter record, reach out to us. We can
      look it up, and either add that contact to your account or guide you to
      update your voter registration using{" "}
      <ExternalLink
        href="https://registertovoteflorida.gov/home"
        target="_blank"
      >
        {"Florida's"} official website
      </ExternalLink>
      .
    </SubNote>
    <SubNote>
      <Emph>3. Your voter status must be {'"Active"'}.</Emph>
      <MiniBr />
      Florida registrations are active by default. The state may mark a
      registration inactive if it believes {"you've"} moved.{" "}
      <ExternalLink
        href="https://registertovoteflorida.gov/home"
        target="_blank"
      >
        Check and update your official voter status
      </ExternalLink>{" "}
      or contact us to check your voter status on our copy of the voter file.
    </SubNote>
    <SubNote>
      <Emph>4. Your voter information is public record.</Emph>
      <MiniBr />
      Under Florida law,{" "}
      <ExternalLink
        href="https://dos.fl.gov/elections/for-voters/voter-registration/voter-information-is-public-record/"
        target="_blank"
      >
        voter information is public with a few exceptions
      </ExternalLink>
      . If your record is confidential, it {"isn't"} in the file we receive, so
      we {"can't"} verify it.
    </SubNote>
    <SubNoteBig>
      Contact us and we can look up your voter record (your voter status,
      contacts on file, etc.) and guide you to get verified based on your
      specific situation. We can also notify you when your voter registration
      appears in an updated voter file.
    </SubNoteBig>
  </Wrapper>
);

export default HowVerificationWorksContent;
